import { createSelector } from 'reselect';
import { getViewIndex } from './commonSelectors';
import { getCurrentSeason } from './seasonSelectors';
import { FHA, FHA_KEY } from '../constants/mapLayerIds';
import { createUnit } from '../i18n/unitQtyWrapper';
import { LOCALE_GROUPS } from '../constants/units';
import { calcPrecipitation } from '../utils/weatherData';
import { precipitationSelector, gduValueForFHAEvent, gduListSelector } from './weatherSelector';

const currentSeasonFHAEvents = (state, props) => {
  const season = getCurrentSeason(state, props);
  const fhaSeasonData =
    state.layers.list[season] && state.layers.list[season].fha;
  return fhaSeasonData ? fhaSeasonData.subLayers : [];
};

const plantingDataSelector = (state, props) => {
  const season = getCurrentSeason(state, props);
  return state.planting[season];
};

export const activeSubLayerData = (state, props) => {
  const season = getCurrentSeason(state, props);
  const index = getViewIndex(state, props);
  const layersList = state.layers.list;
  return (
    layersList[season] &&
    layersList[season].fha &&
    layersList[season].fha.activeSubLayerIds[index]
  );
};

export const hasHistoricWeatherSelector = (state) =>
  state.field.features.includes('HistoricWeather');

export const isFHACurrentlySelected = (state, props) =>
  state.layers.activeMapLayers[props.index].id === FHA_KEY;

export const activeSubLayerSelector = createSelector(
  activeSubLayerData,
  (data) => data
);

export const currentSeasonFHAEventsSelector = createSelector(
  [
    currentSeasonFHAEvents,
    activeSubLayerSelector,
    gduListSelector,
    precipitationSelector,
    plantingDataSelector,
  ],
  (events, activeSubLayer, gdusObj, precipObj = {}, plantingData) => {
    const plantingDate = plantingData && plantingData.date;
    return events.map((event) => {
      const precipLocalized = createUnit(
        calcPrecipitation(precipObj, event.date, plantingDate),
        'millimeter'
      )
        .forLocaleGroup(LOCALE_GROUPS.RAINFALL)
        .toString({ precision: 1 });

      const lowPercent =
        activeSubLayer.segmentSelected === FHA.SCOUTING &&
          event[FHA.SCOUTING].lowPercent !== null
          ? event[FHA.SCOUTING].lowPercent
          : undefined;
      const lowPercentLocalized =
        typeof lowPercent === 'number' &&
        createUnit(lowPercent, 'percent').toString();

      const percentages =
        activeSubLayer.segmentSelected === FHA.SCOUTING &&
          event[FHA.SCOUTING].percentages !== null
          ? event[FHA.SCOUTING].percentages
          : undefined;
      // percentages is not localized as percentage isnt dependant on units;

      const gdu = gduValueForFHAEvent(gdusObj, event);

      return {
        ...event,
        precipitation: precipLocalized,
        ...(lowPercentLocalized && { lowPercent: lowPercentLocalized }),
        percentages,
        ...(gdu && { gdus: gdu }),
      };
    });
  }
);

export const currentFHAEvent = createSelector(
  currentSeasonFHAEvents,
  activeSubLayerData,
  (seasonEvents, activeFHAData) =>
    seasonEvents.find((e) => e.date === activeFHAData.date)
);

/**
 * Returns the active event from the list of currentSeasonFHAEventsSelector
 * NOTE currentFHAEvent() doesn't provided accurate gdus and precipitation values.
 * @param {Object} state
 * @param {Object} props
 * @return {Object} data need to present the GDU and Precipitation to FHA spiltview bottom panel
 */
export const getCurrentFHAEvent = createSelector(
  currentSeasonFHAEventsSelector,
  activeSubLayerData,
  (seasonEvents, activeFHAData) =>
    seasonEvents.find((e) => e.date === activeFHAData.date)
);

export const currentFHAEventSubLayer = createSelector(
  currentFHAEvent,
  activeSubLayerData,
  (event, activeFHAData) => event[activeFHAData.layer]
);

export const latestFHAEvent = createSelector(
  currentSeasonFHAEvents,
  (events) => events[0] || null
);

export const nextEventSelector = createSelector(
  currentSeasonFHAEvents,
  activeSubLayerData,
  (events, activeFHAData) => {
    const index = events.findIndex((e) => e.date === activeFHAData.date);
    return index === 0 ? events[index] : events[index - 1];
  }
);

export const prevEventSelector = createSelector(
  currentSeasonFHAEvents,
  activeSubLayerData,
  (events, activeFHAData) => {
    const index = events.findIndex((e) => e.date === activeFHAData.date);
    return events.length === index + 1 ? events[index] : events[index + 1];
  }
);

export const doesCurrentFHAEventHaveTrueColor = createSelector(
  currentFHAEvent,
  (event) => event && Boolean(event[FHA.TRUE_COLOR])
);

export const isTrueColorEnabled = createSelector(
  doesCurrentFHAEventHaveTrueColor,
  isFHACurrentlySelected,
  (hasTrueColor, isOnFha) => hasTrueColor && isOnFha
);

export const isTrueColorOn = createSelector(
  activeSubLayerData,
  (data) => data.layer === FHA.TRUE_COLOR
);
